import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../../components/ui/card';
import { Skeleton } from '../../../components/ui/skeleton';
import { Badge } from '../../../components/ui/badge';
import { IconChartBar, IconAlertTriangle } from '@tabler/icons-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import coverageVerificationAnalyticService from '../../../services/coverage-verification-analytics.service';

const TabEngagementChart = ({ organization, dateRange, refreshKey }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await coverageVerificationAnalyticService.getTabEngagementAnalysis({
          organization,
          dateRange,
        });
        // Backend returns 'tabs', handle both for compatibility
        setData(result.tabs || result.tab_engagement || []);
      } catch (err) {
        console.error('Failed to fetch tab engagement:', err);
        setError('Failed to load tab engagement data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [organization, dateRange, refreshKey]);

  if (loading) {
    return (
      <Card className="shadow-sm border-0">
        <CardHeader>
          <CardTitle className="text-base font-bold">Tab Engagement Analysis</CardTitle>
          <CardDescription className="text-sm">
            View counts and user engagement across different tabs
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="w-full h-80" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="shadow-sm border-0">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-red-600">
            <IconAlertTriangle size={20} />
            {error}
          </div>
        </CardContent>
      </Card>
    );
  }

  const chartData = data.map((item) => ({
    name: item.tab_name,
    'View Count': item.view_count,
    'Unique Users': item.unique_users,
  }));

  return (
    <Card className="shadow-sm border-0">
      <CardHeader>
        <div className="flex items-center gap-2">
          <IconChartBar size={20} />
          <div className="flex-1">
            <CardTitle className="text-base font-bold">Tab Engagement Analysis</CardTitle>
            <CardDescription className="text-sm">
              View counts and user engagement across different tabs
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">No tab engagement data available</div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="name" className="text-xs" />
                <YAxis className="text-xs" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--background))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '6px'
                  }} 
                />
                <Legend />
                <Bar dataKey="View Count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Unique Users" fill="#22c55e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>

            {/* Detailed Stats Table */}
            <div className="mt-6">
              <h6 className="text-sm font-semibold mb-3">Detailed Statistics</h6>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Tab</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Total Views</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Unique Users</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Avg Time Spent</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Mobile Views</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Desktop Views</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((item, index) => (
                      <tr key={index} className="border-b hover:bg-muted/50 transition-colors">
                        <td className="py-3 px-4">
                          <span className="font-medium capitalize">{item.tab_name}</span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge variant="outline">{item.view_count}</Badge>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge variant="outline" className="bg-green-50">{item.unique_users}</Badge>
                        </td>
                        <td className="py-3 px-4 text-center text-sm">
                          {item.avg_time_on_tab ? `${item.avg_time_on_tab.toFixed(1)}s` : '-'}
                        </td>
                        <td className="py-3 px-4 text-center text-sm text-muted-foreground">
                          {item.mobile_views || 0}
                        </td>
                        <td className="py-3 px-4 text-center text-sm text-muted-foreground">
                          {item.desktop_views || 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TabEngagementChart;
